import * as React from "react";
import { ComponentDemo } from "@/components/docs/component-demo";
import { InstallCommand } from "@/components/docs/install-command";

interface ComponentPageProps {
  title: string;
  description: string;
  preview: React.ReactNode;
  code: string;
  installCommand: string;
  usageCode?: string;
  apiReference?: {
    name: string;
    props: Array<{
      name: string;
      type: string;
      description: string;
    }>;
  };
}

export function ComponentPage({
  title,
  description,
  preview,
  code,
  installCommand,
  usageCode,
  apiReference,
}: ComponentPageProps) {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl font-bold tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-2 text-lg">{description}</p>
      </div>

      <ComponentDemo code={code}>{preview}</ComponentDemo>

      <div className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Installation</h2>
        <p className="text-sm text-muted-foreground">
          Install the component from the registry using the following command:
        </p>
        <InstallCommand command={installCommand} />
      </div>

      <div className="space-y-4">
        <h2 className="text-2xl font-semibold tracking-tight">Usage</h2>
        <p className="text-sm text-muted-foreground">
          Import and use the component in your project:
        </p>
        <div className="rounded-lg border bg-muted p-4">
          <pre className="text-sm">
            <code className="font-mono">{usageCode || code}</code>
          </pre>
        </div>
      </div>

      {apiReference && (
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold tracking-tight">API Reference</h2>
          <div className="space-y-2">
            <div className="rounded-lg border bg-muted p-4">
              <h3 className="font-semibold mb-2">{apiReference.name}</h3>
              <div className="space-y-1 text-sm">
                {apiReference.props.map((prop) => (
                  <div key={prop.name}>
                    <code className="text-primary">{prop.name}: {prop.type}</code>
                    <p className="text-muted-foreground mt-1">{prop.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

